/**
 * Dimensions de la barre d'outils flottante (px de base), calculées plutôt que
 * mesurées : le choix 1 ou 2 colonnes ne dépend que du nombre d'outils
 * visibles par groupe et de la hauteur disponible. Doit suivre le CSS de
 * CanvasToolbar / ToolsMenu (cases de 36 px, écart de 4 px, séparateurs).
 */

/** Case d'un outil : bouton carré de la barre (CanvasButton « md »). */
export const TOOLBAR_BUTTON_SIZE = 36;
/** Écart entre deux cases de la grille, dans les deux sens. */
export const TOOLBAR_GAP = 4;
/** Séparateur de groupes : filet de 1 px et 3 px de marge au-dessus et dessous. */
export const TOOLBAR_DIVIDER_HEIGHT = 7;
/** Marge intérieure verticale de la barre (8 px en haut et en bas) + bordures. */
export const TOOLBAR_VERTICAL_PADDING = 2 * 8 + 2;
/** Marge intérieure horizontale (4 px de chaque côté, bordure droite comprise). */
export const TOOLBAR_HORIZONTAL_PADDING = 8;

export type ToolbarColumns = 1 | 2 | 3;

/**
 * Réglage de la modale Affichage : « auto » = une colonne, deux quand la
 * hauteur ne suffit pas ; « two » = au moins deux colonnes. Trois colonnes ne
 * sont pas un réglage : le dernier recours quand deux ne tiennent pas
 * (téléphone en paysage).
 */
export const TOOLBAR_COLUMNS_MODES = ["auto", "two"] as const;
export type ToolbarColumnsMode = (typeof TOOLBAR_COLUMNS_MODES)[number];

/** Hauteur de la barre pour des groupes de `n` outils visibles. */
export function toolbarHeight(
  groupSizes: readonly number[],
  columns: ToolbarColumns,
): number {
  const groups = groupSizes.filter((size) => size > 0);
  if (!groups.length) return 0;
  const rows = groups.reduce((sum, size) => sum + Math.ceil(size / columns), 0);
  const dividers = groups.length - 1;
  return (
    TOOLBAR_VERTICAL_PADDING +
    rows * TOOLBAR_BUTTON_SIZE +
    dividers * TOOLBAR_DIVIDER_HEIGHT +
    (rows + dividers - 1) * TOOLBAR_GAP
  );
}

/** Largeur de la barre, bordure comprise. */
export function toolbarWidth(columns: ToolbarColumns): number {
  return (
    columns * TOOLBAR_BUTTON_SIZE +
    (columns - 1) * TOOLBAR_GAP +
    TOOLBAR_HORIZONTAL_PADDING
  );
}

/**
 * Le moins de colonnes qui tienne dans la hauteur disponible, à partir d'une
 * (« auto ») ou de deux (« two ») ; trois quand deux ne tiennent toujours pas.
 */
export function chooseToolbarColumns(
  groupSizes: readonly number[],
  availableHeight: number,
  mode: ToolbarColumnsMode = "auto",
): ToolbarColumns {
  const candidates: ToolbarColumns[] = mode === "two" ? [2] : [1, 2];
  const fitting = candidates.find(
    (columns) => toolbarHeight(groupSizes, columns) <= availableHeight,
  );
  return fitting ?? 3;
}
